import React from 'react';
import { FrameBone, Grid, ParagraphBone, WishListToolbar } from '@gik/ui';
import bemBlock from '@gik/core/utils/bemBlock';
import { useInkind } from '@gik/api/inkinds/inkind';
import { useWishlistProducts } from '@gik/api/inkinds/wishlistProducts';
import withSuspense from '@gik/core/components/suspended/withSuspense';
import WishlistEmptyState from './WishlistEmptyState';
import WishlistProductTile from './WishlistProductTile';
import SeeFullWishlistButton from './SeeFullWishlistButton';
import { canUserEditPage } from '@gik/inkind-page/utils/inkindPagePermissions';
import { useUserStore } from '@gik/core/store/UserStore';

export interface IWishlistGridProps {
  inkindRouteId: string;
  isFullWishlistPage?: boolean;
  skeletonItems?: number;
}

const skelBem = bemBlock(`wishlist-grid-skeleton`);
const Skeleton = ({ skeletonItems = 4 }: Partial<IWishlistGridProps>) => (
  <Grid className={`${skelBem()} gik-grid-4-cols`}>
    {Array.from(Array(skeletonItems).keys()).map(index => {
      return (
        <div key={index} className={skelBem('item')}>
          <FrameBone className={skelBem('image')} />
          <main>
            <ParagraphBone words={18} className={skelBem('text')} />
          </main>
        </div>
      );
    })}
  </Grid>
);

function WishlistGrid({ inkindRouteId, isFullWishlistPage }: IWishlistGridProps): React.ReactElement {
  const bem = bemBlock('wishlist-grid');
  const userStore = useUserStore();
  const userId = userStore.id;

  const { data: items, error: wishListProductsError, isValidating: isLoadingWishlistProducts } = useWishlistProducts(
    inkindRouteId
  );

  const { data: inkindPage, error: inkindPageError } = useInkind(inkindRouteId);

  const canEdit = canUserEditPage(inkindPage, userId);

  const maxProductsToDisplay = isFullWishlistPage ? undefined : 24;

  const isLoading = !items || !inkindPage;
  const isError = isLoading && (inkindPageError || wishListProductsError);

  if (isLoading) {
    return <Skeleton />;
  }
  if (isError) {
    return <Skeleton />;
  }

  const hasItems = items?.length > 0;

  return (
    <>
      <Grid className={bem(null, null, 'gik-grid-4-cols')}>
        {items?.slice(0, maxProductsToDisplay)?.map(item => (
          <WishlistProductTile
            key={item.id}
            item={item}
            inkindRouteId={inkindRouteId}
            toolbar={
              <WishListToolbar
                inkindRouteId={inkindRouteId}
                itemId={item.id}
                hasRemoveFromCurrentPageButton={canEdit}
              />
            }
          />
        ))}
      </Grid>
      {!isLoadingWishlistProducts && !hasItems && <WishlistEmptyState inkindRouteId={inkindRouteId} proportion={0.8} />}
      <div className={bem('grid-footer')}>
        {maxProductsToDisplay && maxProductsToDisplay < items?.length && (
          <SeeFullWishlistButton inkindPage={inkindPage} />
        )}
      </div>
    </>
  );
}

export default withSuspense(WishlistGrid, Skeleton);
